import axios from 'axios'

import { get, post, setToken } from '@/api/request'
import type { ApiResponse } from '@/api/request'
import type { User } from '@/types/api'

import type { AxiosRequestConfig } from 'axios'

function parseErrorToApiResponse<T = unknown>(error: unknown): ApiResponse<T> {
  if (
    axios.isAxiosError(error) &&
    error.response?.data &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'success' in error.response.data
  ) {
    return error.response.data as ApiResponse<T>
  }
  const message = error instanceof Error ? error.message : 'Request failed'
  return { success: false, error: message }
}

async function postAuth<T = unknown>(url: string, body?: unknown): Promise<ApiResponse<T>> {
  try {
    const response = await post<T>(url, body)
    return response.data
  } catch (error: unknown) {
    return parseErrorToApiResponse<T>(error)
  }
}

async function getAuth<T = unknown>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  try {
    const response = await get<T>(url, config)
    return response.data
  } catch (error: unknown) {
    return parseErrorToApiResponse<T>(error)
  }
}

export interface LoginParams {
  email: string
  password: string
}

export interface GoogleLoginParams {
  code?: string
  idToken?: string
  referralCode?: string
  channel?: string
  fingerprint?: string
}

export interface VerifyAndRegisterParams {
  email: string
  password: string
  code: string
  referralCode?: string
  channel?: string
  fingerprint?: string
}

export interface ResetPasswordParams {
  email: string
  code: string
  newPassword: string
}

export const authApi = {
  login: (params: LoginParams): Promise<ApiResponse<{ user: User; token: string }>> =>
    postAuth('/auth/login', params),

  register: (params: LoginParams): Promise<ApiResponse<{ user: User; token: string }>> =>
    postAuth('/auth/register', params),

  googleLogin: (
    params: GoogleLoginParams,
  ): Promise<ApiResponse<{ user: User; token: string; isNewUser?: boolean }>> =>
    postAuth('/auth/google', params),

  getMe: (): Promise<ApiResponse<{ user: User }>> => getAuth('/auth/me'),

  sendVerificationCode: (email: string): Promise<ApiResponse> =>
    postAuth('/auth/send-code', { email }),

  /** 与 Next `lib/api/auth.ts`、后端 `POST /auth/verify-and-register` 一致 */
  verifyAndRegister: async (
    params: VerifyAndRegisterParams,
  ): Promise<ApiResponse<{ user: User; token: string }>> => {
    const res = await postAuth<{ user: User; token: string }>('/auth/verify-and-register', params)
    if (res.success && res.data?.token) {
      setToken(res.data.token)
    }
    return res
  },

  forgotPassword: (email: string): Promise<ApiResponse> =>
    postAuth('/auth/forgot-password', { email }),

  resetPassword: (params: ResetPasswordParams): Promise<ApiResponse> =>
    postAuth('/auth/reset-password', params),
}
